import { ApolloError } from '@apollo/client';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@risksmart-app/atomic-ui';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { isTRPCClientError } from '@trpc/client';
import type { FC } from 'react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmModal from 'src/components/confirm-modal/ConfirmModal';

import { useArchiveUser, useRestoreUser } from '@/hooks/mutations/user';
import { useIsCustomerSupport } from '@/hooks/useIsCustomerSupport';

type UserStatusValue = 'active' | 'archived' | null | undefined;

/**
 * Pulls a user-facing reason off a mutation rejection so the CS rep sees the
 * specific cause (e.g. the user's sign-in account no longer exists) rather than
 * the generic failure copy. Only client-level errors (4xx / Hasura action
 * errors) are surfaced; opaque 5xx/internal errors fall back to the generic
 * message so raw infrastructure detail is never shown.
 */
const getActionFailureMessage = (error: unknown): string | undefined => {
  if (isTRPCClientError(error)) {
    const status = error.data?.httpStatus;

    return status !== undefined && status < 500 ? error.message : undefined;
  }
  if (error instanceof ApolloError) {
    const message = error.graphQLErrors[0]?.message;
    if (message && !/internal error|unexpected/i.test(message)) {
      return message;
    }
  }

  return undefined;
};

export interface UserStatusCellProps {
  userId: string;
  /** The org-scoped status to display (falls back to account-level if absent). */
  status: UserStatusValue;
  /** Display name shown in the confirmation dialog. */
  userName: string;
  /**
   * External_Id on the caller org's organisationuser row — non-null means
   * SCIM-provisioned *in this org* (COI-827). The tenant-global
   * `auth.user."External_Id"` is deliberately not consulted: it is shared by
   * every org in the tenant, so it reported another org's IdP as this org's.
   */
  orgUserExternalId: string | null | undefined;
  /** Effective role key — `ThirdPartyRespondent` is managed in the Third Parties area, not here. */
  roleKey: string | null | undefined;
}

/**
 * Renders the Status column for a user in Settings → Users as a small
 * button. The button is enabled only when the caller is a CustomerSupport
 * user acting on a non-SCIM, non-self user — in which case clicking it
 * opens a confirmation dialog directly (no intermediate dropdown), since
 * each row has only one valid transition. SCIM users get a popover
 * explaining that offboarding must go through their IdP.
 */
const UserStatusCell: FC<UserStatusCellProps> = ({
  userId,
  status,
  userName,
  orgUserExternalId,
  roleKey,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'userSettings.archive',
  });
  const isCallerCS = useIsCustomerSupport();
  const { user: currentUser } = useRisksmartUser();
  const { addNotification } = useNotifications();
  const { archiveUser, loading: archiving } = useArchiveUser();
  const { restoreUser, loading: restoring } = useRestoreUser();

  const [confirmOpen, setConfirmOpen] = useState(false);

  // Nullish, not truthy: an empty-string External_Id still means the IdP wrote
  // this membership row, so the user IS SCIM-managed. `''` is reachable —
  // the V3 `PUT /users/{userId}` payload schema (`services/data-layer/src/
  // schemas/user.ts`) does not constrain the value before mirroring it onto
  // the membership row, unlike the org-user create schema which is
  // `.min(1)`. Truthiness here would silently offer CS an archive the server
  // then rejects, and it disagreed with `config.tsx`, which preserves `''`.
  const isScimManaged = orgUserExternalId != null;
  const isThirdPartyContact = roleKey === 'ThirdPartyRespondent';
  const isSelf = currentUser?.userId === userId;
  const displayStatus = status ?? 'active';
  const statusLabel = String(displayStatus);
  const isLoading = archiving || restoring;

  const isInteractive =
    isCallerCS &&
    !isScimManaged &&
    !isThirdPartyContact &&
    !isSelf &&
    (displayStatus === 'active' || displayStatus === 'archived');

  const tooltip = isThirdPartyContact
    ? t('third_party_tooltip')
    : isScimManaged
      ? t('scim_tooltip')
      : isSelf
        ? t('self_tooltip')
        : null;

  const nextAction: 'archive' | 'restore' =
    displayStatus === 'active' ? 'archive' : 'restore';
  const confirmTitle =
    nextAction === 'archive'
      ? t('confirm_archive_title')
      : t('confirm_restore_title');
  const confirmBodyKey =
    nextAction === 'archive'
      ? 'userSettings.archive.confirm_archive_body'
      : 'userSettings.archive.confirm_restore_body';
  const successMessage =
    nextAction === 'archive' ? t('archive_success') : t('restore_success');

  const onConfirm = async () => {
    try {
      if (nextAction === 'archive') {
        await archiveUser(userId);
      } else {
        await restoreUser(userId);
      }
      addNotification({ type: 'success', content: successMessage });
      setConfirmOpen(false);
    } catch (error) {
      addNotification({
        type: 'error',
        content: getActionFailureMessage(error) ?? t('action_failed'),
      });
      setConfirmOpen(false);
    }
  };

  const statusButton = (
    <Button
      size={'sm'}
      radius={'full'}
      disabled={!isInteractive}
      onClick={() => setConfirmOpen(true)}
    >
      {statusLabel}
    </Button>
  );

  return (
    <>
      {isCallerCS && tooltip ? (
        <Popover>
          <PopoverTrigger render={statusButton} />
          <PopoverContent>{tooltip}</PopoverContent>
        </Popover>
      ) : (
        statusButton
      )}
      <ConfirmModal
        isVisible={confirmOpen}
        onConfirm={() => void onConfirm()}
        onDismiss={() => setConfirmOpen(false)}
        header={confirmTitle}
        loading={isLoading}
      >
        <div className={'flex flex-col gap-y-3'}>
          <Trans
            i18nKey={confirmBodyKey}
            values={{ name: userName }}
            components={{ p: <p />, strong: <strong /> }}
          />
        </div>
      </ConfirmModal>
    </>
  );
};

export default UserStatusCell;
